<template>
  <div class="grid justify-content-between my-4">
    <div class="col flex flex-column align-items-start">
      <!-- The toggle that used to live in WellCompletion.vue -->
      <LabelledToggle
        v-model="showDeviationSurvey"
        :text="`${
          deviationSurveyForm.data.value?.id ? 'Show' : 'Add'
        } Deviation Survey`"
      />

      <a
        href="/imperial_directional_data_template.xlsx"
        download="imperial_directional_data_template.xlsx"
        class="mt-3"
      >
        <icon :icon="['far', 'link']" />
        <span class="ml-1">Imperial Directional Data Template</span>
      </a>

      <a
        href="/metric_directional_data_template.xlsx"
        download="metric_directional_data_template.xlsx"
        class="mt-3"
      >
        <icon :icon="['far', 'link']" />
        <span class="ml-1">Metric Directional Data Template</span>
      </a>
    </div>

    <div class="col flex align-items-center">
      <span class="heading-3 w-2 mr-2 text-right">MD</span>
      <ConverterInput
        class="w-full"
        label="Top of Perfs"
        :rule-names="['perf_depth_warning']"
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        mode="write"
        :model-value="wellProperties.top_of_perforations_measured_depth_feet"
        @update:model-value="
          wellPropertiesForm.update({
            top_of_perforations_measured_depth_feet: $event
          })
        "
      />
    </div>

    <div class="col flex align-items-center">
      <span class="heading-3 w-2 mr-2 text-right">TVD</span>
      <ConverterInput
        class="w-full"
        label="Top of Perfs"
        :disabled="hasDeviationSurvey"
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        mode="write"
        :model-value="
          wellProperties.top_of_perforations_true_vertical_depth_feet
        "
        :rule-names="['perf_tvd_warning']"
        @update:model-value="
          wellPropertiesForm.update({
            top_of_perforations_true_vertical_depth_feet: $event
          })
        "
      />
    </div>
  </div>
  <div v-if="user.is_admin" class="col-2 flex-col align-items-center">
    <ConverterInput
      label="Average DLS"
      :base-unit="dlsUnit"
      :disabled="is3DSSurvey"
      :model="[dlsUnit]"
      :decimal-places="4"
      :min="0"
      nullable
      mode="write"
      :model-value="wellPropertiesForm.data.value?.average_dls"
      @update:model-value="
        wellPropertiesForm.update({ average_dls: $event ?? null })
      "
    />
  </div>
  <transition name="vertical-slide">
    <section
      v-show="showDeviationSurvey"
      class="wrapper"
      data-cy="deviation-survey-section"
    >
      <div v-if="hasUploadedNonDeletedSurvey" data-cy="uploaded-survey">
        <div class="flex justify-content-between align-items-center mx-3 my-2">
          <div>
            <span>
              <icon :icon="['fas', 'file-check']" />
              <span class="ml-2" data-cy="survey-title">
                {{
                  deviationSurvey?.title
                    ? deviationSurvey.title
                    : "Deviation Survey"
                }}
              </span>
            </span>
            <span
              v-if="deviationSurveyForm.initialData.value?.created_at"
              class="body-1 ml-2"
              data-cy="uploaded-date"
            >
              Uploaded
              {{
                new Date(
                  deviationSurveyForm.initialData.value.created_at
                ).toDateString()
              }}
            </span>
            <span class="body-1 ml-2" data-cy="survey-type">
              Survey Type: {{ deviationSurveyTypeLabel }}
            </span>
          </div>
          <div>
            <div class="formgroup-inline">
              <div
                v-for="angleUnit of angleUnits"
                :key="angleUnit.label"
                class="field-radiobutton"
              >
                <RadioButton
                  v-model="selectedAngleUnit"
                  name="angleUnit"
                  :value="angleUnit"
                />
                <label :for="angleUnit.label" data-cy="angle-unit">{{
                  angleUnit.label
                }}</label>
              </div>
              <div
                v-for="depthUnit of depthUnits"
                :key="depthUnit.label"
                class="field-radiobutton"
              >
                <RadioButton
                  v-model="selectedDepthUnit"
                  name="depthUnit"
                  :value="depthUnit"
                />
                <label :for="depthUnit.label" data-cy="depth-unit">{{
                  depthUnit.label
                }}</label>
              </div>
              <Button
                class="p-button-danger p-button-outlined"
                data-cy="remove-button"
                @click="onRemove"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
        <DeviationSurveyTable
          v-if="showDeviationSurvey"
          :key="devSurveyTableKey"
          :rowHeight="51.5"
          :survey-data="deviationSurvey.entry_rows"
          :survey-direction-type="deviationSurvey.directional_data_set"
          :is-preview="false"
          :depth-unit="selectedDepthUnit"
          :angle-unit="selectedAngleUnit"
        />
      </div>
      <div v-else data-cy="no-survey">
        <DeviationSurveyPreview :sizing-unit="selectedDepthUnit" />
      </div>
    </section>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import DeviationSurveyPreview from "./DeviationSurveyPreview.vue";
import DeviationSurveyTable from "./DeviationSurveyTable.vue";
import { Degrees, Radians } from "@/libs/units/angle";
import {
  Feet,
  Meters,
  LargeLengthUnits,
  LengthUnits
} from "@/libs/units/length";
import { injectStrict } from "@/utils/utils";
import {
  scenarioKey,
  scenarioCalculationsKey,
  scenarioValidatorKey,
  deviationSurveyFormKey,
  wellPropertiesFormKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { Deviation_Survey_Directional_Dataset_Enum } from "gql/api";
import { extrapolateTVDByMD } from "@/libs/calculations/deviationSurvey";
import useUserInfo from "@/composables/auth/useUserInfo";
import type { ConvertableUnit } from "@/libs/units";

const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellProperties = computed(() => wellPropertiesForm.data.value);

const scenario = injectStrict(scenarioKey);
const calcs = injectStrict(scenarioCalculationsKey);
const sizing = injectStrict(sizingKey, null);
const { filterByXrefIdAndRuleNames } = injectStrict(scenarioValidatorKey);
const user = useUserInfo();

const deviationSurveyForm = injectStrict(deviationSurveyFormKey);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);
const largeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");

const dlsUnit = computed<ConvertableUnit>(() => {
  const isFt = largeLengthUnit.value === Feet;
  const diameter = isFt ? `100ft` : `30m`;
  return {
    label: `deg/${diameter}`,
    toStandardUnit: (dls: number) => dls,
    toThisUnit: dls => dls
  };
});

const showDeviationSurvey = ref(false);
const hasDeviationSurvey = computed(() => {
  return !!(
    !deviationSurveyForm.data.value?.deleted_at &&
    deviationSurveyForm.data.value?.entry_rows
  );
});

const deviationSurvey = computed(() => deviationSurveyForm.data.value);

const devSurveyTableKey = computed(() => {
  return `
    ${calcs.equipmentLengths.tubingLengthFt.value}
    ${calcs.equipmentLengths.bottomOfEquipmentDepthFt.value}
    ${filterByXrefIdAndRuleNames(["install_dls"]).value.length}
    ${
      filterByXrefIdAndRuleNames(["landing_dls", "landing_dls_caution"]).value
        .length
    }
    ${filterByXrefIdAndRuleNames(["landing_inclination"]).value.length}
  `;
});

const hasUploadedNonDeletedSurvey = computed(() => {
  return deviationSurvey.value.id && !deviationSurvey.value.deleted_at;
});
const hasExistingSavedSurvey = computed(() => {
  return !!deviationSurveyForm.initialData.value?.id;
});

const depthUnits = [Feet, Meters];
const angleUnits = [Degrees, Radians];
const selectedDepthUnit = ref(
  largeLengthUnit.value.label === Feet.label ? Feet : Meters
);
const selectedAngleUnit = ref(Degrees);

watch(
  () => largeLengthUnit.value,
  newValue => {
    if ([Feet.label, Meters.label].includes(newValue.label)) {
      selectedDepthUnit.value = newValue;
    } else {
      selectedDepthUnit.value = largeLengthUnit.value;
    }
  }
);

const is3DSSurvey = computed(() => {
  return (
    hasDeviationSurvey.value &&
    wellPropertiesForm.data.value.r_deviation_survey?.directional_data_set ===
      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
  );
});

watch(is3DSSurvey, val => {
  if (val) {
    wellPropertiesForm.update({
      average_dls: null
    });
  }
});

function onRemove() {
  if (hasExistingSavedSurvey.value) {
    deviationSurveyForm.update({ deleted_at: "now()" });
  } else {
    deviationSurveyForm.reset();
  }
  wellPropertiesForm.update({ deviation_survey_id: null });
}

const deviationSurveyTypeLabel = computed(() => {
  if (
    deviationSurvey.value?.directional_data_set === undefined ||
    deviationSurvey.value?.directional_data_set === null
  ) {
    return "Unknown";
  }
  switch (deviationSurvey.value.directional_data_set) {
    case Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth:
      return "3D MD Inclination Azimuth";
    case Deviation_Survey_Directional_Dataset_Enum.TwoDMdInclination:
      return "2D MD Inclination";
    case Deviation_Survey_Directional_Dataset_Enum.TwoDMdTvd:
      return "2D MD TVD";
    default:
      return deviationSurvey.value.directional_data_set;
  }
});

watch(
  () => wellPropertiesForm.data.value?.top_of_perforations_measured_depth_feet,
  (val: number) => {
    if (hasDeviationSurvey.value) {
      wellPropertiesForm.update({
        top_of_perforations_true_vertical_depth_feet: extrapolateTVDByMD(
          val,
          calcs.entryRows.value
        )
      });
    }
  }
);
</script>

<style lang="scss" scoped>
.wrapper {
  border-radius: var(--border-radius);
  border: 1px solid var(--border-main);
  background: var(--surface-0);
  padding: 0.75rem;
}
</style>
